import { ObjectID } from 'bson';
import mongoose, { Schema } from 'mongoose';
import isEmail from 'validator/lib/isEmail';
import { UserLevel } from '../constants';
import ratingSchema, { IRating } from './rating';
import serviceLocation, { IServiceLocation } from './serviceLocation';
import telephoneSchema, { ITelephone } from './telephone';

export interface IUser {
  _id: ObjectID;
  externalUserId: string;
  externalProvider: string;
  username?: string;
  email?: string;
  name?: string;
  kycValidated?: number;
  kycToken?: string;
  kycApplicationId?: string;
  myReferralCode?: string;
  referrerCode?: string;
  plsBalance?: number;
  biography?: string;
  profilePicture?: string;
  level: UserLevel;
  rating: IRating;
  website?: string;
  phone?: ITelephone;
  address?: IServiceLocation;
  isTravelPlanner: boolean;
  travelPlanningPricePerDay: number;
  travelPlanningLocations: IServiceLocation[];
  languages: string[];
  whitelistedIcoAddresses: string[];
  createdAt: Date;
  updatedAt: Date;
  __v?: number;
}

/**
 * We have this interface with just email because
 * an anonymous user can just add and email and call it a day
 * in that case we need to allow him to book trips
 */
export interface IUserWithEmail extends IUser {
  email: string;
}

export interface IAuthUser extends IUser {
  username: string;
  email: string;
  myReferralCode: string;
  level: Exclude<UserLevel, UserLevel.anonymous>;
}

export interface IUserDocument extends mongoose.Document, IUser {
  _id: ObjectID;
}

const user = new Schema(
  {
    authData: {
      type: Schema.Types.Mixed,
      required: false,
    },
    externalUserId: {
      type: String,
      required: true,
    },
    externalProvider: {
      type: String,
      required: true,
    },
    username: {
      type: String,
      required: requiredIfNotAnonymous,
      trim: true,
      unique: true,
      sparse: true,
    },
    email: {
      type: String,
      required: requiredIfNotAnonymous,
      trim: true,
      unique: true,
      sparse: true,
      validate: {
        validator: isEmail,
      },
    },
    name: {
      type: String,
      required: false,
      trim: true,
    },
    kycValidated: {
      type: Number,
      required: false,
      default: 0,
    },
    kycToken: {
      type: String,
      required: false,
    },
    kycApplicationId: {
      type: String,
      required: false,
    },
    myReferralCode: {
      type: String,
      required: requiredIfNotAnonymous,
      trim: true,
      unique: true,
      sparse: true,
    },
    referrerCode: {
      type: String,
      required: false,
    },
    city: {
      type: String,
      required: false,
    },
    country: {
      type: String,
      required: false,
    },
    plsBalance: {
      type: Number,
      required: false,
      default: 0,
    },
    metamaskPublicAddress: {
      type: String,
      required: false,
    },
    ledgerPublicAddress: {
      type: String,
      required: false,
    },
    nonce: {
      type: String,
      required: false,
    },
    biography: {
      type: String,
      required: false,
    },
    profilePicture: {
      type: String,
      required: false,
    },
    level: {
      type: String,
      enum: Object.values(UserLevel),
      required: true,
      default: UserLevel.regular,
    },
    rating: {
      type: ratingSchema,
      default: ratingSchema,
      required: true,
    },
    website: {
      type: String,
      required: false,
      trim: true,
    },
    phone: {
      type: telephoneSchema,
      required: false,
    },
    address: {
      type: serviceLocation,
      required: false,
    },
    isTravelPlanner: {
      type: Boolean,
      default: false,
    },
    travelPlanningPricePerDay: {
      type: Number,
      default: 0,
    },
    travelPlanningLocations: {
      type: [serviceLocation],
      required: false,
    },
    languages: {
      type: [String],
      validate: {
        validator: (value: string[]) => {
          if (!value.every(v => v.length === 2 || v.length === 3)) return false;
          return true;
        },
      },
    },
    whitelistedIcoAddresses: [
      {
        type: String,
        required: true,
      },
    ],
  },
  { timestamps: true },
);

user.index({ externalUserId: 1, externalProvider: 1 });

function requiredIfNotAnonymous(this: IUser) {
  if (this.level === UserLevel.anonymous) return false;
  return true;
}

export default user;
